import {FC, useState, useMemo, useEffect} from "react";
import {
    Box,
    Checkbox,
    CloseButton,
    Combobox,
    Flex,
    Highlight,
    Pill,
    PillsInput,
    rem,
    Tooltip,
    useCombobox,
    Anchor
} from "@mantine/core";
import {
    IconCheck,
    IconMailCheck,
    IconMailOff,
    IconMessage2Check,
    IconMessage2Off,
    IconSearch,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import Fetch from "@/utils/Fetch";
import EmployeeService from "@/services/employee/employee-service";
import * as Enums from "@/utils/enums";
import styles from './RecipientSelector.module.css';
import EmployeeAvatar from "@/PageComponents/Table/EmployeeAvatar";
import { Contact, Employee } from "./NewMessageForm";

export type RecipientType = 'contact' | 'employee';

export interface Recipient {
    ID: string;
    FullName: string;
    FirstName: string;
    EmailAddress: string;
    MobileNumber: string;
    SendEmail: boolean;
    SendSMS: boolean;
    type: RecipientType;
    isAssigned?: boolean;
    IsPrimary?: boolean;
    IsPrimaryAccount?: boolean;
    DisplayColor?: string;
}

interface RecipientSelectorProps {
    moduleCode: string | number;
    itemId: string;
    messageType: number;
    value?: Recipient[];
    onChange?: (recipients: Recipient[]) => void;
    placeholder?: string;
    error?: string | null;
    preSelectEmployeeId?: string;
    autoSelectDefaults?: boolean;
    onBlur?: () => void;
    onInitialized?: () => void;
}

export const getCanSendToPersonError: (
    messageType: number,
    person: Employee | Contact
) => string | null = (messageType: any, person: any) => {

    const emailError = !person.EmailAddress ? 'No email address' :
        !person.SendEmail ? 'Excluded from emails' : null

    const smsError = !person.MobileNumber ? 'No mobile number' : !person.SendSMS ? 'Excluded from SMSes' : null
    const bothError = emailError && smsError ? (emailError + '. ' + smsError) : (emailError || smsError)

    return (
        messageType === Enums.MessageType.Email
    ) ? emailError : (
        messageType === Enums.MessageType.SMS
    ) ? smsError : bothError
}

const RecipientSelector: FC<RecipientSelectorProps> = ({
    moduleCode,
    itemId,
    messageType,
    value = [],
    onChange,
    placeholder = "Search all employees or related contacts",
    error,
    preSelectEmployeeId,
    autoSelectDefaults = true,
    onBlur,
    onInitialized
}) => {
    const combobox = useCombobox();
    const [search, setSearch] = useState('');
    const [showAllEmployees, setShowAllEmployees] = useState(false);

    const getItemUrl = () => {
        switch (parseInt(moduleCode as any)) {
            case Enums.Module.Customer: return { itemUrl: '/Customer/', isSupplier: false, isPurchaseOrder: false };
            case Enums.Module.JobCard: return { itemUrl: '/Job/', isSupplier: false, isPurchaseOrder: false };
            case Enums.Module.Asset: return { itemUrl: '/Product?id=', isSupplier: false, isPurchaseOrder: false };
            case Enums.Module.Query: return { itemUrl: '/Query/', isSupplier: false, isPurchaseOrder: false };
            case Enums.Module.Quote: return { itemUrl: '/Quote/', isSupplier: false, isPurchaseOrder: false };
            case Enums.Module.Invoice: return { itemUrl: '/Invoice/', isSupplier: false, isPurchaseOrder: false };
            case Enums.Module.Project: return { itemUrl: '/Project/', isSupplier: false, isPurchaseOrder: false };
            case Enums.Module.Supplier: return { itemUrl: '/Supplier/', isSupplier: true, isPurchaseOrder: false };
            case Enums.Module.PurchaseOrder: return { itemUrl: '/PurchaseOrder/', isSupplier: true, isPurchaseOrder: true };
            default: return { itemUrl: '', isSupplier: false, isPurchaseOrder: false };
        }
    };

    const { itemUrl, isSupplier, isPurchaseOrder } = useMemo(() => getItemUrl(), [moduleCode]);

    const { data: item, isFetched: itemFetched } = useQuery(
        ['new-comm-item', itemUrl, itemId],
        () => Fetch.get({ url: itemUrl + itemId }),
        { enabled: !!itemUrl && !!itemId }
    );

    const defaultContactId = useMemo(() => {
        return isSupplier ? item?.SupplierContact?.ID : item?.Contact?.ID;
    }, [item, isSupplier]);

    const { data: customerData, isFetched: customerFetched } = useQuery(
        ['new-comm-customer', item?.CustomerID, moduleCode, isSupplier],
        async () => {
            if (isSupplier) return null;
            if (!moduleCode || parseInt(moduleCode as any) === 0) return item;
            return Fetch.get({ url: '/Customer/' + item.CustomerID });
        },
        { enabled: !!item && !isSupplier }
    );

    const { data: supplierData, isFetched: supplierFetched } = useQuery(
        ['new-comm-supplier', item?.SupplierID, isPurchaseOrder, isSupplier],
        async () => {
            if (!isSupplier) return null;
            if (isPurchaseOrder) return Fetch.get({ url: `/Supplier/${item.SupplierID}` });
            return item;
        },
        { enabled: !!item && isSupplier }
    );

    const { data: employeesData, isFetched: employeesFetched } = useQuery(
        ['new-comm-employees', item?.StoreID],
        () => EmployeeService.getEmployees(item.StoreID),
        { enabled: !!item }
    );

    const allRecipients = useMemo(() => {
        const recipients: Recipient[] = [];

        // Add Employees
        if (employeesData?.Results) {
            const assignedEmployeeIds = item?.Employees?.map((e: any) => e.ID) || [];
            employeesData.Results.forEach((e: Employee) => {
                recipients.push({
                    ID: e.ID,
                    FullName: e.FullName,
                    FirstName: e.FirstName,
                    EmailAddress: e.EmailAddress,
                    MobileNumber: e.MobileNumber,
                    SendEmail: e.SendEmail,
                    SendSMS: e.SendSMS,
                    type: 'employee',
                    isAssigned: assignedEmployeeIds.includes(e.ID),
                    DisplayColor: e.DisplayColor
                });
            });
        }

        // Add Contacts
        const target = isSupplier ? supplierData : customerData;
        if (target?.Contacts) {
            target.Contacts.forEach((c: Contact) => {
                recipients.push({
                    ID: c.ID,
                    FullName: c.FullName,
                    FirstName: c.FirstName,
                    EmailAddress: c.EmailAddress,
                    MobileNumber: c.MobileNumber,
                    SendEmail: c.SendEmail,
                    SendSMS: c.SendSMS,
                    type: 'contact',
                    IsPrimary: c.IsPrimary,
                    IsPrimaryAccount: c.IsPrimaryAccount
                });
            });
        }

        // Add specific contact from item if not already present
        const itemContact = isSupplier ? item?.SupplierContact : item?.Contact;
        if (itemContact && !recipients.find(r => r.ID === itemContact.ID)) {
            recipients.push({
                ID: itemContact.ID,
                FullName: itemContact.FullName,
                FirstName: itemContact.FirstName,
                EmailAddress: itemContact.EmailAddress,
                MobileNumber: itemContact.MobileNumber,
                SendEmail: itemContact.SendEmail,
                SendSMS: itemContact.SendSMS,
                type: 'contact',
                IsPrimary: itemContact.IsPrimary,
                IsPrimaryAccount: itemContact.IsPrimaryAccount
            });
        }

        return recipients;
    }, [employeesData, customerData, supplierData, item, isSupplier]);

    const [hasPreselected, setHasPreselected] = useState(false);

    useEffect(() => {
        if (!hasPreselected) {
            const itemFetchEnabled = !!itemUrl && !!itemId;

            if (!itemFetchEnabled) {
                setHasPreselected(true);
                onInitialized?.();
                return;
            }

            if (itemFetched) {
                if (!item) {
                    setHasPreselected(true);
                    onInitialized?.();
                    return;
                }

                const dataLoaded = isSupplier ? supplierFetched : customerFetched;
                const employeesLoaded = employeesFetched;

                if (dataLoaded && employeesLoaded) {
                    if (autoSelectDefaults && allRecipients.length > 0) {
                        const defaults: Recipient[] = [];

                        if (preSelectEmployeeId) {
                            const emp = allRecipients.find(x => x.type === 'employee' && x.ID === preSelectEmployeeId);
                            if (emp) defaults.push(emp);
                        }

                        // Add specific contact from item if present (high priority)
                        if (defaultContactId) {
                            const r = allRecipients.find(x => x.ID === defaultContactId);
                            // preselect the current contact for the item, otherwise preselect the primary contact
                            if (r) {
                                defaults.push(r)
                            } else {
                                allRecipients.forEach(r => {
                                    if (defaults.find(d => d.ID === r.ID)) return;

                                    // Primary contacts
                                    if (r.type === 'contact' && (r.IsPrimary)) {
                                        defaults.push(r);
                                    }
                                });
                            }
                        }

                        if (defaults.length > 0 && value.length === 0) {
                            onChange?.(defaults);
                        }
                    }
                    setHasPreselected(true);
                    onInitialized?.();
                }
            }
        }
    }, [allRecipients, customerFetched, supplierFetched, employeesFetched, autoSelectDefaults, hasPreselected, value.length, onChange, item, isSupplier, onInitialized, itemFetched, itemUrl, itemId, defaultContactId]);

    const filteredRecipients = useMemo(() => {
        const s = search.toLowerCase().trim();
        if (!s) {
            return allRecipients;
        }

        return allRecipients.filter(r => 
            r.FullName?.toLowerCase().includes(s) ||
            r.EmailAddress?.toLowerCase().includes(s) ||
            r.MobileNumber?.toLowerCase().includes(s)
        );
    }, [allRecipients, search]);

    const groupedRecipients = useMemo(() => {
        const employeesRaw = filteredRecipients.filter(r => r.type === 'employee')
            .sort((a, b) => {
                if (a.isAssigned && !b.isAssigned) return -1;
                if (!a.isAssigned && b.isAssigned) return 1;
                return a.FullName.localeCompare(b.FullName);
            });

        const contacts = filteredRecipients.filter(r => r.type === 'contact')
            .sort((a, b) => {
                if (a.ID === defaultContactId) return -1;
                if (b.ID === defaultContactId) return 1;

                const aPrimary = a.IsPrimary || a.IsPrimaryAccount;
                const bPrimary = b.IsPrimary || b.IsPrimaryAccount;
                if (aPrimary && !bPrimary) return -1;
                if (!aPrimary && bPrimary) return 1;
                return a.FullName.localeCompare(b.FullName);
            });

        const s = search.toLowerCase().trim();
        let employees = employeesRaw;
        let hasMoreEmployees = false;

        if (!s && !showAllEmployees) {
            const assignedCount = employeesRaw.filter(e => e.isAssigned).length;
            const limit = Math.max(3, assignedCount);
            if (employeesRaw.length > limit) {
                employees = employeesRaw.slice(0, limit);
                hasMoreEmployees = true;
            }
        }

        return { employees, contacts, hasMoreEmployees };
    }, [filteredRecipients, search, showAllEmployees, defaultContactId]);

    const handleValueSelect = (val: string) => {
        const recipient = allRecipients.find(r => r.ID === val);
        if (!recipient) return;

        const isSelected = value.some(v => v.ID === val);
        if (isSelected) {
            onChange?.(value.filter(v => v.ID !== val));
        } else {
            onChange?.([...value, recipient]);
        }
        setSearch('');
    };

    const handleValueRemove = (val: string) => {
        const newValue = value.filter(v => v.ID !== val);
        if (newValue.length !== value.length) {
            onChange?.(newValue);
        }
    };

    const values = value.map(v => v.ID);

    const renderOption = (r: Recipient) => {
        const error = getCanSendToPersonError(messageType, r as any);
        const isPrimary = r.IsPrimary;

        const canSendEmail = !!r.EmailAddress && r.SendEmail;
        const canSendSMS = !!r.MobileNumber && r.SendSMS;

        let isDisabled = false;
        if (messageType === Enums.MessageType.Email) {
            isDisabled = !canSendEmail;
        } else if (messageType === Enums.MessageType.SMS) {
            isDisabled = !canSendSMS;
        } else if (messageType === Enums.MessageType.Both) {
            isDisabled = !canSendEmail && !canSendSMS;
        }

        return (
            <Combobox.Option
                value={r.ID}
                key={r.ID}
                active={values.includes(r.ID)}
                className={styles.option}
                h={50}
                disabled={isDisabled}
                styles={{
                    option: {
                        backgroundColor: isDisabled ? 'var(--mantine-color-gray-2)' : undefined
                    }
                }}
            >
                <Flex align={'center'} gap={8} h={'100%'}>
                    <Checkbox
                        checked={values.includes(r.ID)}
                        onChange={() => {}}
                        aria-hidden
                        tabIndex={-1}
                        style={{ pointerEvents: 'none' }}
                        color={/*error ? 'gray.6' : */'scBlue'}
                        iconColor={error ? "yellow" : 'white'}
                        icon={({ indeterminate, ...others }: any) =>
                            /*error ? (
                                messageType === Enums.MessageType.SMS ? (
                                    <IconMessage2Exclamation {...others} stroke={4} />
                                ) : (
                                    <IconMailExclamation {...others} stroke={4} />
                                )
                            ) : */
                            (
                                <IconCheck {...others} stroke={4} />
                            )
                        }
                        size="sm"
                    />
                    {r.type === 'employee' && (
                        <EmployeeAvatar color={r.DisplayColor} name={r.FullName} size={1} />
                    )}
                    <Flex direction={'column'} w={'100%'}>
                        <Flex gap={5} align={'center'}  w={'100%'}>
                            <Flex direction="column">
                                <Flex align="center" gap={3} wrap={'wrap'}>
                                    <Highlight mr={5} size={'sm'} highlight={search} color={'scBlue.1'}>
                                        {r.FullName}
                                    </Highlight>
                                    {/*{r.type === 'employee' && r.isAssigned && (
                                        <Tooltip label="Assigned to job" withArrow>
                                            <Box mr={4} mt={4}>
                                                <IconUserPin size={14} color="var(--mantine-color-scBlue-6)" />
                                            </Box>
                                        </Tooltip>
                                    )}*/}
                                    {/*{r.type === 'contact' && isPrimary && (
                                        <Tooltip label="Primary contact" withArrow>
                                            <Box mr={4} mt={2}>
                                                <IconUserStar size={12} color="var(--mantine-color-scBlue-6)" />
                                            </Box>
                                        </Tooltip>
                                    )}*/}
                                </Flex>
                                {(r.MobileNumber || r.EmailAddress) && (
                                    <Highlight size="sm" c={isDisabled ? 'dark' : 'gray'} highlight={search} color={'scBlue.1'}>
                                        {(r.EmailAddress || '') + (r.EmailAddress && r.MobileNumber ? ' - ' : '') + (r.MobileNumber || '')}
                                    </Highlight>
                                )}
                            </Flex>

                            <Tooltip
                                disabled={!error}
                                label={error}
                                events={{ hover: true, focus: true, touch: true }}
                                openDelay={500}
                                color={'yellow.6'}
                            >
                                <Flex align={'center'} gap={5} ml={'auto'}>
                                    {(r.EmailAddress && r.SendEmail) ?
                                        <IconMailCheck color={'var(--mantine-color-scBlue-6)'} size={14} stroke={1.6} /> :
                                        <IconMailOff color={!isDisabled ? 'var(--mantine-color-gray-6)' : 'var(--mantine-color-dark-9)'} size={14} stroke={1.6} />
                                    }
                                    {(r.MobileNumber && r.SendSMS) ?
                                        <IconMessage2Check color={'var(--mantine-color-scBlue-6)'} size={14} stroke={1.6} /> :
                                        <IconMessage2Off color={!isDisabled ? 'var(--mantine-color-gray-6)' : 'var(--mantine-color-dark-9)'} size={14} stroke={1.6} />
                                    }
                                </Flex>
                            </Tooltip>
                        </Flex>
                    </Flex>
                </Flex>
            </Combobox.Option>
        );
    };

    return (
        <Combobox
            store={combobox}
            onOptionSubmit={handleValueSelect}
            withinPortal
            position={"bottom-start"}
        >
            <Combobox.DropdownTarget>
                <PillsInput
                    styles={{input: {backgroundColor: 'var(--mantine-color-gray-0)', border: `1px solid ${error ? 'var(--mantine-color-yellow-6)' : 'var(--mantine-color-gray-2)'}`}}}
                    error={error}
                    onClick={() => combobox.openDropdown()}
                    leftSection={<IconSearch size={18} color={error ? 'var(--mantine-color-yellow-6)' : 'var(--mantine-color-gray-6)'} />}
                >
                    <Pill.Group>
                        {value.map((r) => {
                            const error = getCanSendToPersonError(messageType, r as any);
                            return (
                                <Tooltip
                                    key={r.ID}
                                    disabled={!error}
                                    label={error}
                                    events={{ hover: true, focus: true, touch: true }}
                                    openDelay={700}
                                    color={'yellow.6'}
                                >
                                    <Box
                                        style={{
                                            display: 'flex',
                                            cursor: 'default',
                                            alignItems: 'center',
                                            color: error ? 'var(--mantine-color-yellow-7)' : 'var(--mantine-color-gray-7)',
                                            backgroundColor: '#fff',
                                            border: `1px solid ${error ? 'var(--mantine-color-yellow-6)' : 'var(--mantine-color-gray-2)'}`,
                                            borderRadius: 8,
                                            paddingLeft: 5,
                                        }}
                                    >
                                        <Flex align={'center'} justify={'start'} gap={5}>
                                            {r.type === 'employee' ? (
                                                <EmployeeAvatar color={r.DisplayColor} name={r.FullName} size={.8} />
                                            ) : null}
                                            <Box style={{ lineHeight: 1, fontSize: rem(12) }}>{r.FullName}</Box>
                                        </Flex>
                                        <CloseButton
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                handleValueRemove(r.ID);
                                            }}
                                            variant={'transparent'}
                                            size={22}
                                            iconSize={14}
                                            tabIndex={-1}
                                            style={{ color: 'inherit' }}
                                        />
                                    </Box>
                                </Tooltip>
                            );
                        })}

                        <Combobox.EventsTarget>
                            <PillsInput.Field
                                onFocus={() => combobox.openDropdown()}
                                onBlur={() => {
                                    combobox.closeDropdown();
                                    onBlur?.();
                                }}
                                value={search}
                                placeholder={value.length === 0 ? placeholder : ''}
                                onChange={(event) => {
                                    combobox.updateSelectedOptionIndex();
                                    setSearch(event.currentTarget.value);
                                    setShowAllEmployees(false);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Backspace' && search.length === 0) {
                                        event.preventDefault();
                                        const lastItem = value[value.length - 1];
                                        if (lastItem) {
                                            handleValueRemove(lastItem.ID);
                                        }
                                    }
                                }}
                            />
                        </Combobox.EventsTarget>
                    </Pill.Group>
                </PillsInput>
            </Combobox.DropdownTarget>

            <Combobox.Dropdown>
                <Combobox.Options>
                    {groupedRecipients.employees.length > 0 && (
                        <Combobox.Group label="Employees">
                            {groupedRecipients.employees.map(renderOption)}
                            {groupedRecipients.hasMoreEmployees && (
                                <Box px="md" py="xs">
                                    <Anchor
                                        component="button"
                                        size="sm"
                                        fw={500}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => setShowAllEmployees(true)}
                                    >
                                        Show all employees
                                    </Anchor>
                                </Box>
                            )}
                        </Combobox.Group>
                    )}

                    {groupedRecipients.contacts.length > 0 && (
                        <Combobox.Group label="Contacts">
                            {groupedRecipients.contacts.map(renderOption)}
                        </Combobox.Group>
                    )}

                    {filteredRecipients.length === 0 && (
                        <Combobox.Empty>
                            {search.trim() === ''
                                ? 'No recipients found. Type to search.'
                                : 'Nothing found....'}
                        </Combobox.Empty>
                    )}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
};

export default RecipientSelector;
